import type { SupabaseClient } from '@supabase/supabase-js';
import type { Version, Profile, EntityStatus } from '@/types/types';

export async function getAllVersions(client: SupabaseClient, userId: string): Promise<Version[]> {
    const { data } = await client.from('versions').select('*').eq('user_id', userId).order('start_date', { ascending: false });
    return data || [];
}

export async function getVersionById(client: SupabaseClient, userId: string, id: string): Promise<Version | null> {
    const { data, error } = await client
        .from('versions')
        .select('*')
        .eq('id', id)
        .eq('user_id', userId)
        .single();
    if (error) {
        if (error.code === 'PGRST116') return null;
        console.error('getVersionById error:', error.message);
        return null;
    }
    return data as Version | null;
}

export async function createVersion(
    client: SupabaseClient,
    userId: string,
    data: { title: string; description: string; number: number; startDate: string },
    profile: Profile | null,
    invalidateProfile: () => void,
    EntityStatusEnum: typeof EntityStatus,
    options?: { setActive?: boolean },
): Promise<Version> {
    const shouldActivate = options?.setActive ?? true;
    const defaultDays = profile?.version_days_default || 90;
    const start = new Date(data.startDate);
    const end = new Date(start);
    end.setDate(start.getDate() + defaultDays);

    const { data: newVersion, error } = await client.from('versions').insert({
        user_id: userId,
        number: data.number,
        title: data.title,
        description: data.description,
        status: EntityStatusEnum.ACTIVE,
        start_date: data.startDate,
        end_date: end.toISOString().split('T')[0]
    }).select().single();

    if (error) throw error;

    if (shouldActivate) {
        await client.from('profiles').update({ current_version_id: newVersion.id }).eq('id', userId);
        invalidateProfile();
    }

    return newVersion;
}

export async function updateVersion(client: SupabaseClient, userId: string, version: Version): Promise<void> {
    const { error } = await client.from('versions').update({
        number: version.number,
        title: version.title,
        description: version.description,
        start_date: version.start_date,
        end_date: version.end_date,
        status: version.status
    }).eq('id', version.id).eq('user_id', userId);
    if (error) throw error;
}

export async function deleteVersion(client: SupabaseClient, userId: string, versionId: string): Promise<void> {
    const { error } = await client.from('versions').delete().eq('id', versionId).eq('user_id', userId);
    if (error) throw error;
}

export async function setVersionActive(client: SupabaseClient, userId: string, versionId: string, invalidateProfile: () => void): Promise<void> {
    await client.from('profiles').update({ current_version_id: versionId }).eq('id', userId);
    invalidateProfile();
}
